import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { FYMAPIError } from 'fym-common/src/generics/errors/FYMAPIError';
import { useEndReelsPlanMutation, useMakeReelsPlanMutation } from '../../../../../../redux/api/fymAPI';
import type { ReelsPlanSettings } from '../../../../../../redux/api/fymAPI.types';
import { useVideoCreditGate, type VideoCreditGate } from '../../../credits/useVideoCreditGate';
import { studioActionErrorFeedback, type StudioActionFeedback } from '../../../studio/studioActionErrorText';
import type { SocialPlatform } from '../../../types';

interface UseReelsPlanProps {
  accountId: number;
  catalogItemUuid: string;
}

export interface ReelsPlanActions {
  gate: VideoCreditGate;
  /**
   * "Make the plan": the credit check first. Missing credits of the dry run (`missingCredits`) or a
   * short balance open the packs and send nothing. It answers true when the plan was made.
   */
  make: (
    settings: ReelsPlanSettings,
    platforms: SocialPlatform[],
    credits: number,
    missingCredits?: number
  ) => Promise<boolean>;
  /** "End the plan". It answers true when the plan ended. */
  end: () => Promise<boolean>;
  isMaking: boolean;
  isEnding: boolean;
  /** The sentence of a failed "Make the plan", for the place next to its button. */
  makeFeedback: StudioActionFeedback | null;
  endFeedback: StudioActionFeedback | null;
  /** A control changed: the old sentence goes. */
  clearFeedback: () => void;
}

/**
 * The two writes of the AI reels plan (design rollout-plan f1, f4). A failed write gives its
 * sentence for the place next to the button, never a toast (owner rule 2026-10-06). A 402 opened
 * the plan or credit prompt already (`customBaseQuery`), so it gives no sentence.
 */
export function useReelsPlan({ accountId, catalogItemUuid }: UseReelsPlanProps): ReelsPlanActions {
  const intl = useIntl();
  const gate = useVideoCreditGate(accountId);
  const [makePlan, { isLoading: isMaking }] = useMakeReelsPlanMutation();
  const [endPlan, { isLoading: isEnding }] = useEndReelsPlanMutation();
  const [makeFeedback, setMakeFeedback] = useState<StudioActionFeedback | null>(null);
  const [endFeedback, setEndFeedback] = useState<StudioActionFeedback | null>(null);
  const { canSpend, offerCredits } = gate;

  const make = useCallback(
    async (
      settings: ReelsPlanSettings,
      platforms: SocialPlatform[],
      credits: number,
      missingCredits = 0
    ): Promise<boolean> => {
      if (missingCredits > 0) {
        offerCredits();
        return false;
      }
      if (!canSpend(credits)) {
        return false;
      }
      setMakeFeedback(null);
      try {
        await makePlan({ accountId, catalogItemUuid, ...settings, platforms }).unwrap();
        return true;
      } catch (caught: unknown) {
        if (!(caught instanceof FYMAPIError && caught.statusCode === 402)) {
          setMakeFeedback(studioActionErrorFeedback(intl, 'makeReelsPlan', caught));
        }
        return false;
      }
    },
    [accountId, canSpend, catalogItemUuid, intl, makePlan, offerCredits]
  );

  const end = useCallback(async (): Promise<boolean> => {
    setEndFeedback(null);
    try {
      await endPlan({ accountId, catalogItemUuid }).unwrap();
      return true;
    } catch (caught: unknown) {
      setEndFeedback(studioActionErrorFeedback(intl, 'endReelsPlan', caught));
      return false;
    }
  }, [accountId, catalogItemUuid, endPlan, intl]);

  const clearFeedback = useCallback(() => {
    setMakeFeedback(null);
    setEndFeedback(null);
  }, []);

  return { gate, make, end, isMaking, isEnding, makeFeedback, endFeedback, clearFeedback };
}
